import { useQuery } from '@wyz/utils';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import type {
  IErrorApiResponse,
  IFilter,
  IPaginationResponse,
  ISorter,
} from '@wyz/types';
import {
  DatatableProps,
  DatatableState,
  IFilterForm,
  RowModel,
  UseDatatableResult,
} from './types';
import {
  DatatableUrlState,
  parseDatatableSearch,
  serializeDatatableState,
} from './url-state';
import {
  formatDefaultValues,
  formToCondition,
  transformCriteria,
} from './utils';
import { isEqual, isNil } from 'lodash-es';
type Options<DataItem extends RowModel, TCriteria = IFilter> = Pick<
  DatatableProps<DataItem, TCriteria>,
  | 'fetcher'
  | 'defaults'
  | 'queryKey'
  | 'queryTransformer'
  | 'columns'
  | 'qsAware'
>;

const DEFAULT_SORTER: ISorter = {
  sortField: 'id',
  sortDirection: 'DESC',
};

const DEFAULT_FILTER: DatatableState['criteria'] = {
  conditions: [],
  operator: 'or',
};

export const DEFAULT_PER_PAGE = 20;

type InitialDatatableState = Omit<
  DatatableState,
  'transformedCriteria' | 'formValues'
>;
const INITIAL_STATE: InitialDatatableState = {
  limit: DEFAULT_PER_PAGE,
  page: 1,
  sort: DEFAULT_SORTER,
  criteria: DEFAULT_FILTER,
  visibleColumns: [],
};

const useDatatable = <DataItem extends RowModel, TCriteria = IFilter>({
  fetcher,
  queryKey,
  defaults,
  columns,
  queryTransformer,
  qsAware = false,
}: Options<DataItem, TCriteria>): UseDatatableResult<DataItem, TCriteria> => {
  const initialValue: InitialDatatableState = {
    ...INITIAL_STATE,
    ...defaults,
    //   visibleColumns: columns.map((column) => column.field),
  };
  const [searchParams, setSearchParams] = useSearchParams();

  const filterableColumns = columns.filter((column) => column.filterable);
  /**
   * The baseline the url is read against: filter values the url does not
   * mention fall back to these, and values still equal to them stay out of the
   * url entirely.
   */
  const urlDefaults: DatatableUrlState = {
    page: initialValue.page,
    limit: initialValue.limit,
    sort: initialValue.sort,
    formData: formatDefaultValues(
      filterableColumns,
      initialValue.criteria?.conditions,
    ),
  };

  /**
   * Builds the criteria driven part of the state out of raw filter form values.
   * Shared by the initial state and by `preSubmit` so that a state restored from
   * the url goes through the exact same transformation as a submitted form.
   */
  const buildCriteria = (formData?: IFilterForm) => {
    const criteria = {
      ...initialValue.criteria,
      conditions: formToCondition(filterableColumns, formData),
      query:
        !isNil(formData?.searchQuery) && formData?.searchQuery !== ''
          ? String(formData?.searchQuery).trim()
          : undefined,
    };
    return {
      criteria,
      transformedCriteria: transformCriteria<TCriteria>(
        criteria,
        queryTransformer,
        formData,
      ),
      formValues: formData ?? {},
    };
  };

  const [paginatorState, setPaginatorState] = useState<
    DatatableState<TCriteria>
  >(() => {
    const restored = qsAware
      ? parseDatatableSearch(searchParams.toString(), urlDefaults)
      : urlDefaults;
    // Compared by value, not by "does the query string hold anything": a param
    // that belongs to the page rather than to the datatable must not count as a
    // filter, otherwise it would wipe the defaults.
    const urlChangedFilters = !isEqual(restored.formData, urlDefaults.formData);

    return {
      page: restored.page,
      limit: restored.limit,
      sort: {
        sortField: restored.sort?.sortField || 'id',
        sortDirection: restored.sort?.sortDirection || 'DESC',
      },
      visibleColumns: [],
      // As long as the url leaves the filters at their defaults, the criteria
      // built by the provider is kept as-is: it derives each condition's
      // operator from `isMulti` while `formToCondition` reads
      // `column.filter.operator`, and those disagree on several tables, so
      // rebuilding it here would quietly change what is sent to the api.
      ...(qsAware && urlChangedFilters
        ? buildCriteria(restored.formData)
        : {
            criteria: initialValue.criteria,
            transformedCriteria: transformCriteria<TCriteria>(
              initialValue.criteria,
              queryTransformer,
            ),
            formValues: urlDefaults.formData,
          }),
    };
  });

  useEffect(() => {
    if (!qsAware) {
      return;
    }
    const next = serializeDatatableState(
      {
        page: paginatorState.page,
        limit: paginatorState.limit,
        sort: paginatorState.sort,
        formData: paginatorState.formValues,
      },
      urlDefaults,
    );
    // Both sides go through `URLSearchParams` before being compared: the
    // router encodes a space as `+` while `qs` encodes it as `%20`, so the raw
    // strings would never match and every render would rewrite the url.
    if (new URLSearchParams(next).toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    qsAware,
    paginatorState.page,
    paginatorState.limit,
    paginatorState.sort,
    paginatorState.formValues,
  ]);

  const query = useQuery<IPaginationResponse<DataItem>, IErrorApiResponse>({
    queryKey: [
      ...queryKey,
      paginatorState.page,
      paginatorState.limit,
      paginatorState.sort,
      paginatorState.criteria,
      paginatorState.transformedCriteria,
    ],
    refetchOnMount: true,
    queryFn: () => {
      return fetcher({
        limit: paginatorState.limit,
        sort: paginatorState.sort,
        page: paginatorState.page,
        criteria: paginatorState.transformedCriteria,
      });
    },
    gcTime: 0,
    staleTime: 0,

    //   ...DEFAULT_QUERY_OPTIONS,
    //initialData: INITIAL_PAGINATION_RESPONSE<DataItem>(),
    // keepPreviousData: true,
  });
  const preSubmit: UseDatatableResult<DataItem, TCriteria>['preSubmit'] = (
    formData,
  ) => {
    return {
      ...paginatorState,
      page: 1,
      ...buildCriteria(formData),
    };
  };
  return {
    query,
    state: paginatorState,
    setter: setPaginatorState,
    preSubmit: preSubmit,
  };
};

export default useDatatable;
